import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Phone,
  Activity,
  Calendar,
  Shield,
  FileText,
  Users,
  UserCheck,
  Clock,
  MapPin,
  Briefcase,
  Hospital,
  Droplet,
  HeartPulse,
  ShieldCheck,
  Repeat,
  Building2,
  FileCheck,
  Handshake,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { mockData, images } from '../mock';
import { iconMap } from '../lib/icons';
import FAQAccordion from '../components/FAQAccordion';
import Seo from '../components/Seo';

// Core NEMT services surfaced on the homepage (must communicate MoveraCare's capabilities in < 5 seconds)
const CORE_SERVICES = [
  { icon: 'Wheelchair', title: 'Wheelchair Transportation', desc: 'Wheelchair-accessible vans with proper securement. Drivers assist passengers during transport.' },
  { icon: 'Car', title: 'Ambulatory Transportation', desc: 'Comfortable sedan and van transport for ambulatory patients.' },
  { icon: 'Briefcase', title: "Workers' Compensation Transportation", desc: 'Authorized transportation for injured workers to approved medical destinations.' },
  { icon: 'Activity', title: 'Dialysis Transportation', desc: 'Reliable recurring rides for dialysis treatment schedules.' },
  { icon: 'Hospital', title: 'Hospital Discharge Transportation', desc: 'Same-day and scheduled discharge transport to home, SNF, or rehab.' },
  { icon: 'HeartPulse', title: 'Therapy & Rehabilitation Transportation', desc: 'Transport for physical therapy, occupational therapy, and rehab appointments.' },
  { icon: 'Repeat', title: 'Recurring Medical Transportation', desc: 'Consistent, scheduled rides for ongoing medical care.' },
  { icon: 'Clock', title: 'Wait-and-Return Transportation', desc: 'Driver waits during shorter appointments and returns the patient home.' },
];

// Healthcare & Transportation Partner audience cards (each links to its dedicated partner sub-page)
const PARTNER_AUDIENCES = [
  {
    id: 'workers-compensation',
    icon: Briefcase,
    title: "Workers' Compensation",
    desc: 'Authorized transportation for injured workers to approved medical, therapy, and diagnostic appointments.',
    to: '/healthcare-partners/workers-compensation',
  },
  {
    id: 'healthcare-facilities',
    icon: Hospital,
    title: 'Healthcare Facilities',
    desc: 'Hospitals, clinics, SNFs, and outpatient centers coordinating patient transportation.',
    to: '/healthcare-partners/healthcare-facilities',
  },
  {
    id: 'insurance-tpa',
    icon: ShieldCheck,
    title: 'Insurance & TPAs',
    desc: 'A transportation provider resource for organizations administering authorized NEMT benefits.',
    to: '/healthcare-partners/insurance-tpa',
  },
  {
    id: 'nemt-brokers',
    icon: FileCheck,
    title: 'NEMT Brokers & Networks',
    desc: 'Reliable transportation capacity for organizations managing NEMT networks and trip assignments.',
    to: '/healthcare-partners/nemt-brokers',
  },
  {
    id: 'dialysis-centers',
    icon: Droplet,
    title: 'Dialysis Centers',
    desc: 'Dedicated recurring wheelchair and ambulatory transportation for dialysis patients.',
    to: '/healthcare-partners/dialysis-centers',
  },
  {
    id: 'hospital-discharge',
    icon: Building2,
    title: 'Hospital Discharge Teams',
    desc: 'Fast, coordinated discharge transportation to home, SNF, rehab, or assisted living.',
    to: '/healthcare-partners/hospital-discharge',
  },
  {
    id: 'case-managers',
    icon: Users,
    title: 'Case Managers & Coordinators',
    desc: 'A direct transportation resource for case managers, adjusters, and care coordinators.',
    to: '/healthcare-partners/case-managers',
  },
  {
    id: 'referral-partners',
    icon: Handshake,
    title: 'Referral Partners',
    desc: 'Community organizations, providers, and coordinators that refer patients for NEMT.',
    to: '/healthcare-partners/referral-partners',
  },
];

// Reasons to choose MoveraCare — "Why MoveraCare"
const WHY_MOVERACARE = [
  { icon: HeartPulse, title: 'Healthcare-Focused Operations', desc: 'Workflows designed around medical appointments and healthcare coordination.' },
  { icon: Users, title: 'Wheelchair & Ambulatory Capability', desc: 'Trained drivers supporting ambulatory and wheelchair passengers with dignity.' },
  { icon: Repeat, title: 'Built for Recurring Care', desc: 'Dialysis, therapy, rehabilitation, and other recurring transportation schedules.' },
  { icon: ShieldCheck, title: 'Credentialing-Ready Documentation', desc: 'Licensed, insured, HIPAA-conscious, and prepared for procurement review.' },
  { icon: Phone, title: 'Responsive Dispatch', desc: 'Clear communication between coordinators, facilities, patients, and drivers.' },
  { icon: FileCheck, title: 'Operational Documentation', desc: 'Trip records that support authorized transportation and billing workflows.' },
];

const Home = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">
      <Seo
        title="Greater Houston NEMT & Wheelchair Transportation | MoveraCare"
        description="MoveraCare Mobility provides professional wheelchair and ambulatory non-emergency medical transportation across Greater Houston. Based in Richmond, TX. Call 832-925-2128."
        path="/"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'MoveraCare Mobility',
            url: 'https://www.moveracaremobility.com',
            publisher: { '@id': 'https://www.moveracaremobility.com/#organization' },
          },
          {
            '@context': 'https://schema.org',
            '@type': ['MedicalBusiness', 'LocalBusiness'],
            '@id': 'https://www.moveracaremobility.com/#organization',
            name: 'MoveraCare Mobility',
            legalName: 'MoveraCare Services LLC',
            alternateName: 'MoveraCare Services LLC',
            url: 'https://www.moveracaremobility.com',
            telephone: '+1-832-925-2128',
            email: 'dispatch@moveracaremobility.com',
            image: 'https://www.moveracaremobility.com/og-image.png',
            logo: 'https://www.moveracaremobility.com/og-image.png',
            priceRange: '$$',
            description:
              'MoveraCare Mobility (MoveraCare Services LLC) is a Richmond, TX-based non-emergency medical transportation provider serving Fort Bend County, Harris County, Sugar Land, Missouri City, Katy, Rosenberg, Stafford, Fulshear, and Greater Houston.',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Richmond',
              addressRegion: 'TX',
              addressCountry: 'US',
            },
            areaServed: [
              { '@type': 'AdministrativeArea', name: 'Fort Bend County, Texas' },
              { '@type': 'AdministrativeArea', name: 'Harris County, Texas' },
              { '@type': 'City', name: 'Richmond, TX' },
              { '@type': 'City', name: 'Sugar Land, TX' },
              { '@type': 'City', name: 'Missouri City, TX' },
              { '@type': 'City', name: 'Katy, TX' },
              { '@type': 'City', name: 'Rosenberg, TX' },
              { '@type': 'City', name: 'Stafford, TX' },
              { '@type': 'City', name: 'Fulshear, TX' },
              { '@type': 'City', name: 'Houston, TX' },
            ],
            openingHoursSpecification: [
              { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'], opens: '06:00', closes: '19:00' },
              { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Sunday'], opens: '12:00', closes: '17:00' },
            ],
            contactPoint: [{
              '@type': 'ContactPoint',
              telephone: '+1-832-925-2128',
              email: 'dispatch@moveracaremobility.com',
              contactType: 'dispatch',
              areaServed: 'US-TX',
              availableLanguage: ['English', 'Spanish'],
            }],
            hasOfferCatalog: {
              '@type': 'OfferCatalog',
              name: 'Non-Emergency Medical Transportation',
              itemListElement: [
                { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Wheelchair Transportation', url: 'https://www.moveracaremobility.com/services/wheelchair-transportation' } },
                { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Ambulatory Transportation', url: 'https://www.moveracaremobility.com/services/ambulatory-transportation' } },
                { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Dialysis Transportation', url: 'https://www.moveracaremobility.com/services/dialysis-transportation' } },
                { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Hospital Discharge Transportation', url: 'https://www.moveracaremobility.com/services/hospital-discharge' } },
                { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Therapy & Rehabilitation Transportation', url: 'https://www.moveracaremobility.com/services/therapy-rehabilitation-transportation' } },
                { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Recurring Medical Transportation', url: 'https://www.moveracaremobility.com/services/recurring-medical-transportation' } },
              ],
            },
          },
        ]}
      />
      {/* 1. HERO ================================================================= */}
      <section className="relative min-h-screen flex items-center justify-center bg-[#004C97] pt-24" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={images.hero}
            alt="Non-Emergency Medical Transportation across Greater Houston"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#004C97] via-[#004C97]/95 to-[#004C97]/80"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#004C97]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl">
            <div className="inline-block px-4 py-2 bg-[#F9A825]/10 border border-[#F9A825]/30 rounded-full mb-6">
              <span className="text-[#F9A825] text-sm font-medium tracking-wider" data-testid="hero-eyebrow">
                HEALTHCARE-FOCUSED NEMT • GREATER HOUSTON
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-heading">
              Non-Emergency Medical Transportation in Richmond, TX &amp; Greater Houston
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 mb-4 leading-relaxed">
              MoveraCare Mobility is based in Richmond, TX and provides wheelchair and ambulatory non-emergency medical transportation for patients, healthcare facilities, Workers&apos; Compensation programs, case managers, NEMT networks, dialysis centers, and hospital discharge teams across Fort Bend County, Harris County, and Greater Houston.
            </p>
            <p className="text-sm md:text-base text-white/90 mb-6 leading-relaxed" data-testid="hero-capability-strip">
              <Link to="/services/wheelchair-transportation" className="underline decoration-[#F9A825]/50 underline-offset-2 hover:decoration-[#F9A825] hover:text-white transition-colors" data-testid="hero-link-wheelchair">Wheelchair</Link>
              {' · '}
              <Link to="/services/ambulatory-transportation" className="underline decoration-[#F9A825]/50 underline-offset-2 hover:decoration-[#F9A825] hover:text-white transition-colors" data-testid="hero-link-ambulatory">Ambulatory</Link>
              {' · '}
              <Link to="/healthcare-partners/workers-compensation" className="underline decoration-[#F9A825]/50 underline-offset-2 hover:decoration-[#F9A825] hover:text-white transition-colors" data-testid="hero-link-workers-comp">Workers&apos; Comp</Link>
              {' · '}
              <Link to="/services/dialysis-transportation" className="underline decoration-[#F9A825]/50 underline-offset-2 hover:decoration-[#F9A825] hover:text-white transition-colors" data-testid="hero-link-dialysis">Dialysis</Link>
              {' · '}
              <Link to="/services/hospital-discharge" className="underline decoration-[#F9A825]/50 underline-offset-2 hover:decoration-[#F9A825] hover:text-white transition-colors" data-testid="hero-link-hospital-discharge">Hospital Discharge</Link>
              {' · '}
              <Link to="/services/therapy-rehabilitation-transportation" className="underline decoration-[#F9A825]/50 underline-offset-2 hover:decoration-[#F9A825] hover:text-white transition-colors" data-testid="hero-link-therapy">Therapy &amp; Rehab</Link>
              {' · '}
              <Link to="/services/recurring-medical-transportation" className="underline decoration-[#F9A825]/50 underline-offset-2 hover:decoration-[#F9A825] hover:text-white transition-colors" data-testid="hero-link-recurring">Recurring</Link>
              {' · Wait-and-Return'}
            </p>
            <p className="text-base text-[#F9A825] mb-8 font-semibold" data-testid="hero-service-area">
              Serving Greater Houston: Fort Bend County · Harris County · Richmond · Sugar Land · Missouri City · Katy · Rosenberg · Stafford · Fulshear
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-8">
              <Link to="/book">
                <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all" data-testid="hero-cta-book">
                  Book a Ride
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/request-patient-transportation">
                <Button size="lg" className="bg-white text-[#004C97] hover:bg-gray-100 font-bold text-lg px-8 py-6 shadow-lg" data-testid="hero-cta-refer">
                  Refer a Patient
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/healthcare-partners">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#004C97] font-semibold text-lg px-8 py-6" data-testid="hero-cta-partners">
                  Healthcare Partners
                </Button>
              </Link>
              <Link to="/procurement">
                <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-[#004C97] font-semibold text-lg px-8 py-6" data-testid="hero-cta-procurement">
                  Procurement / Credentialing
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap gap-3 text-white/90 text-sm">
              {['Licensed & Insured', 'Wheelchair Accessible', 'HIPAA Conscious'].map((badge) => (
                <div key={badge} className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/20">
                  <span className="text-[#F9A825]">✓</span>
                  <span>{badge}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar (visual anchor between hero and audience routing) */}
      <section className="bg-[#F9A825] py-8" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {mockData.trustBadges.map((badge) => {
              const IconComponent = iconMap[badge.icon];
              return (
                <div key={badge.title} className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-[#004C97] rounded-lg flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-6 h-6 text-[#F9A825]" />
                  </div>
                  <div>
                    <h3 className="text-[#004C97] font-semibold text-sm">{badge.title}</h3>
                    <p className="text-[#004C97]/70 text-xs">{badge.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 2. HOW CAN WE HELP? — Audience Routing =================================== */}
      <section className="py-16 md:py-20 bg-white border-b-2 border-gray-100" data-testid="audience-routing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-3">How Can We Help?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              MoveraCare Mobility supports patients, healthcare professionals, and institutional partners.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border-2 border-gray-100 hover:border-[#F9A825] transition-all p-6 flex flex-col" data-testid="audience-patient">
              <div className="w-12 h-12 rounded-lg bg-[#F9A825] flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-[#004C97] mb-2">I Need Transportation</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">For patients and families booking a ride.</p>
              <Link to="/book"><Button className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-semibold w-full" data-testid="cta-audience-book-ride">Book a Ride<ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
            </div>
            <div className="bg-white rounded-xl border-2 border-gray-100 hover:border-[#F9A825] transition-all p-6 flex flex-col" data-testid="audience-professional">
              <div className="w-12 h-12 rounded-lg bg-[#F9A825] flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-[#004C97] mb-2">I&apos;m a Healthcare Professional</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">Refer a patient or coordinate transportation.</p>
              <Link to="/request-patient-transportation"><Button className="bg-[#004C97] text-white hover:bg-[#003876] font-semibold w-full" data-testid="cta-audience-request-patient">Refer a Patient<ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
            </div>
            <div className="bg-white rounded-xl border-2 border-gray-100 hover:border-[#F9A825] transition-all p-6 flex flex-col" data-testid="audience-organization">
              <div className="w-12 h-12 rounded-lg bg-[#F9A825] flex items-center justify-center mb-4">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-[#004C97] mb-2">I&apos;m a Healthcare Organization</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">Facility, TPA, broker, or Workers&apos; Comp partnerships.</p>
              <Link to="/healthcare-partners"><Button className="bg-[#004C97] text-white hover:bg-[#003876] font-semibold w-full" data-testid="cta-audience-partner">Healthcare Partners<ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
            </div>
            <div className="bg-white rounded-xl border-2 border-[#F9A825] hover:shadow-lg transition-all p-6 flex flex-col" data-testid="audience-procurement">
              <div className="w-12 h-12 rounded-lg bg-[#004C97] flex items-center justify-center mb-4">
                <FileCheck className="w-6 h-6 text-[#F9A825]" />
              </div>
              <h3 className="text-lg font-bold text-[#004C97] mb-2">I&apos;m Evaluating MoveraCare</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">Access procurement, credentialing, and vendor resources.</p>
              <Link to="/procurement"><Button className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-semibold w-full" data-testid="cta-audience-procurement">Procurement Portal<ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* 3. OUR TRANSPORTATION SERVICES =========================================== */}
      <section className="py-20 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">OUR TRANSPORTATION SERVICES</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">Non-Emergency Medical Transportation Capabilities</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A full spectrum of wheelchair and ambulatory NEMT services for patients, healthcare facilities, and institutional partners across Greater Houston.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CORE_SERVICES.map((service) => {
              const IconComponent = iconMap[service.icon] || Activity;
              return (
                <Card key={service.title} className="border-2 hover:border-[#F9A825] transition-all hover:shadow-lg" data-testid={`service-card-${service.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <CardHeader>
                    <div className="w-14 h-14 bg-[#F9A825]/10 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="w-7 h-7 text-[#F9A825]" />
                    </div>
                    <CardTitle className="text-lg text-[#004C97] leading-tight">{service.title}</CardTitle>
                    <CardDescription className="text-gray-600 text-sm leading-relaxed">{service.desc}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/services">
              <Button size="lg" className="bg-[#004C97] text-white hover:bg-[#003D7A] font-semibold shadow-md" data-testid="cta-view-all-services">
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. HEALTHCARE & TRANSPORTATION PARTNERS ================================= */}
      <section className="py-20 bg-gray-50" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">HEALTHCARE &amp; TRANSPORTATION PARTNERS</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">Purpose-Built Coordination for Healthcare Organizations</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              MoveraCare works with hospitals, TPAs, NEMT brokers, Workers&apos; Compensation programs, dialysis centers, and case managers to coordinate reliable non-emergency transportation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PARTNER_AUDIENCES.map((partner) => {
              const Icon = partner.icon;
              return (
                <Link key={partner.id} to={partner.to} className="group" data-testid={`partner-card-${partner.id}`}>
                  <Card className="h-full border-2 hover:border-[#F9A825] transition-all hover:shadow-lg">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-[#F9A825] flex items-center justify-center mb-3">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <CardTitle className="text-base text-[#004C97] leading-tight">{partner.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 leading-relaxed mb-3">{partner.desc}</p>
                      <span className="inline-flex items-center text-sm font-semibold text-[#F9A825] group-hover:text-[#E08E1E] transition-colors">
                        Explore <ArrowRight className="ml-1 w-4 h-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/healthcare-partners">
              <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold shadow-md" data-testid="cta-view-all-partners">
                View All Healthcare Partner Programs
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 5. WHY MOVERACARE ======================================================== */}
      <section className="py-20 bg-white" data-testid="why-moveracare-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHY MOVERACARE</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">Healthcare Transportation Infrastructure You Can Rely On</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Operations engineered for healthcare coordination, credentialing readiness, and patient dignity.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_MOVERACARE.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="flex items-start space-x-4 p-5 rounded-xl border-2 border-gray-100 hover:border-[#F9A825] transition-all bg-white">
                  <div className="w-12 h-12 bg-[#F9A825] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#004C97] mb-1">{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6. GREATER HOUSTON SERVICE AREA ========================================== */}
      <section className="py-20 bg-[#004C97]" data-testid="service-area-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MapPin className="w-10 h-10 text-[#F9A825] mx-auto mb-4" />
          <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">VERIFIED SERVICE AREA</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Greater Houston Service Area</h2>
          <p className="text-lg text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Verified coverage across <strong className="text-white">Fort Bend County and Harris County</strong>. Richmond, TX is our base and core operating area — including Sugar Land, Missouri City, Katy, Rosenberg, Stafford, Fulshear, and surrounding Greater Houston communities. Extended-distance assignments may be accepted with advance authorization.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {[
              { name: 'Houston', to: '/houston-tx-medical-transportation' },
              { name: 'Richmond', to: '/richmond-tx-medical-transportation' },
              { name: 'Sugar Land', to: '/sugar-land-tx-medical-transportation' },
              { name: 'Katy', to: '/katy-tx-medical-transportation' },
              { name: 'Missouri City', to: '/missouri-city-tx-medical-transportation' },
              { name: 'Stafford', to: null },
              { name: 'Rosenberg', to: null },
              { name: 'Pearland', to: null },
            ].map((city) =>
              city.to ? (
                <Link key={city.name} to={city.to} className="px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-full text-sm text-white transition-colors" data-testid={`service-area-link-${city.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  {city.name}
                </Link>
              ) : (
                <span key={city.name} className="px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-sm text-white">
                  {city.name}
                </span>
              )
            )}
          </div>
          <div className="flex items-center justify-center gap-2 text-gray-200 text-sm">
            <Clock className="w-4 h-4 text-[#F9A825]" />
            <span>Mon–Sat 6:00 AM – 7:00 PM · Sunday 12:00 PM – 5:00 PM</span>
          </div>
        </div>
      </section>

      {/* 7. PROCUREMENT & CREDENTIALING =========================================== */}
      <section className="py-16 md:py-20 bg-white" data-testid="procurement-cta-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border-2 border-[#F9A825] bg-gradient-to-br from-white to-[#FFF8E7] shadow-xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center p-8 md:p-10">
              <div className="lg:col-span-2">
                <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">FOR PROCUREMENT &amp; CREDENTIALING TEAMS</p>
                <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] mb-3 leading-tight">
                  Considering MoveraCare for Your Provider Network?
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Access MoveraCare&apos;s company capabilities, service coverage, credentialing information, insurance documentation, NPI, and vendor procurement resources — everything a procurement team needs to evaluate MoveraCare as a healthcare transportation provider.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
                  <li className="flex items-center gap-2"><span className="text-[#F9A825] font-bold">✓</span> NPI &amp; Business Documentation</li>
                  <li className="flex items-center gap-2"><span className="text-[#F9A825] font-bold">✓</span> Insurance &amp; Licensing</li>
                  <li className="flex items-center gap-2"><span className="text-[#F9A825] font-bold">✓</span> Service Coverage &amp; Fleet</li>
                  <li className="flex items-center gap-2"><span className="text-[#F9A825] font-bold">✓</span> Credentialing Contacts</li>
                </ul>
              </div>
              <div className="flex flex-col gap-3">
                <a
                  href="/MoveraCare-Vendor-Procurement-Packet.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  data-testid="home-cta-download-packet"
                >
                  <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold w-full whitespace-normal h-auto py-3 leading-tight shadow-lg">
                    Download Provider / Vendor Procurement Packet
                    <ArrowRight className="ml-2 w-4 h-4 flex-shrink-0" />
                  </Button>
                </a>
                <Link to="/procurement">
                  <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold w-full whitespace-normal h-auto py-3" data-testid="home-cta-procurement">
                    View Procurement Portal
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. VERIFIED HEALTHCARE / NETWORK RELATIONSHIPS =========================== */}
      <section className="py-16 md:py-20 bg-gray-50" data-testid="verified-relationships-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">VERIFIED RELATIONSHIPS</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-3">Trusted Healthcare &amp; Network Relationships</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              MoveraCare Mobility maintains verified certifications and healthcare network relationships that support credentialed transportation.
            </p>
          </div>

          {/* Prime Health — Featured Network Partner (largest, most prominent) */}
          <div className="bg-white border-2 border-[#004C97] rounded-2xl shadow-lg overflow-hidden mb-6" data-testid="verified-prime-health">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center p-8">
              <div className="flex items-center justify-center lg:justify-start">
                <div className="bg-white rounded-xl p-3 shadow-md border border-gray-100">
                  <img
                    src="/prime-health.png"
                    alt="Prime Health Services — Network Partner"
                    className="h-24 md:h-28 w-auto object-contain"
                    loading="lazy"
                    data-testid="prime-health-logo-home"
                  />
                </div>
              </div>
              <div className="lg:col-span-2 text-center lg:text-left">
                <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">NETWORK PARTNER</p>
                <h3 className="text-2xl md:text-3xl font-extrabold text-[#004C97] mb-2 leading-tight">
                  In Network with Prime Health Services
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  MoveraCare Mobility is an approved transportation provider for <strong>Prime Health Services</strong> — streamlined authorization and billing workflows for covered Workers&apos; Compensation and healthcare transportation.
                </p>
              </div>
            </div>
          </div>

          {/* MBE Certified — secondary but still credentialing-relevant */}
          <div className="bg-white border-2 border-[#F9A825] rounded-2xl shadow-md overflow-hidden" data-testid="verified-mbe">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center p-6 md:p-8">
              <div className="flex items-center justify-center lg:justify-start">
                <img
                  src="/mbe-certified.webp"
                  alt="City of Houston Certified Minority Business Enterprise (MBE)"
                  className="h-20 md:h-24 w-auto object-contain"
                  loading="lazy"
                  data-testid="mbe-logo-home"
                />
              </div>
              <div className="lg:col-span-2 text-center lg:text-left">
                <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">CITY OF HOUSTON CERTIFIED</p>
                <h3 className="text-xl md:text-2xl font-extrabold text-[#004C97] mb-2 leading-tight">
                  Certified Minority Business Enterprise (MBE)
                </h3>
                <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                  Officially certified by the City of Houston — supporting supplier diversity, inclusion, and healthcare transportation excellence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 9. COMMUNITY SPONSORSHIPS — reduced prominence, below Prime Health ======= */}
      <section className="py-10 bg-[#FAFAF7] border-t border-gray-100" data-testid="community-sponsorship-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <span className="text-[10px] sm:text-xs font-semibold text-[#F9A825] uppercase" style={{ letterSpacing: '0.25em' }}>
              Community Sponsorship
            </span>
            <span className="hidden sm:inline text-gray-300">·</span>
            <a
              href="https://daplittlevogue.com"
              target="_blank"
              rel="noopener noreferrer sponsored"
              aria-label="Community sponsor: Daplittlevogue"
              className="group inline-flex items-center gap-3 hover:opacity-100"
              data-testid="community-sponsor-link"
            >
              <img
                src="/daplittlevogue-logo.webp"
                alt="Daplittlevogue"
                className="h-8 w-auto object-contain opacity-80 group-hover:opacity-100 transition-opacity"
                loading="lazy"
                data-testid="community-sponsor-logo"
              />
              <span className="text-sm text-gray-700 group-hover:text-[#004C97] transition-colors">
                Proud Community Sponsor: <span className="font-semibold text-[#004C97]">Daplittlevogue</span>
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* 10. FINAL CONVERSION CTA ================================================= */}
      <section className="py-20 bg-[#F9A825]" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-6">
            Need Dependable Non-Emergency Medical Transportation?
          </h2>
          <p className="text-xl text-[#004C97]/85 mb-8">
            Book a ride, refer a patient, or start a healthcare partnership with MoveraCare Mobility today.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
            <Link to="/book">
              <Button size="lg" className="bg-[#004C97] text-white hover:bg-[#003D7A] font-semibold text-lg px-8 py-6 shadow-lg" data-testid="final-cta-book">
                Book a Ride
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/request-patient-transportation">
              <Button size="lg" className="bg-white text-[#004C97] hover:bg-gray-100 font-semibold text-lg px-8 py-6 shadow-lg" data-testid="final-cta-refer">
                Refer a Patient
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/healthcare-partners">
              <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold text-lg px-8 py-6" data-testid="final-cta-partners">
                Healthcare Partners
              </Button>
            </Link>
            <a href="tel:+18329252128">
              <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold text-lg px-8 py-6" data-testid="final-cta-call">
                <Phone className="mr-2 w-5 h-5" />
                (832) 925-2128
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ (supporting content) ================================================= */}
      <section className="py-20 bg-gray-50" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Answers about scheduling, wheelchair transportation, facility partnerships, and NEMT services across Greater Houston.
            </p>
          </div>

          <FAQAccordion
            faqs={[
              { question: 'How do I schedule a ride?', answer: 'Call dispatch at (832) 925-2128 or use our online booking form to request transportation quickly and securely.' },
              { question: 'What areas do you serve?', answer: 'MoveraCare Mobility is based in Richmond, TX and serves Fort Bend County, Harris County, Sugar Land, Missouri City, Katy, Rosenberg, Stafford, Fulshear, and Greater Houston.' },
              { question: 'Do you provide wheelchair transportation?', answer: 'Yes. We operate wheelchair-accessible vehicles with proper wheelchair securement. Drivers assist passengers during transport. Please share the wheelchair type, weight, and mobility level when booking so dispatch can confirm we are able to serve the trip.' },
              { question: 'Do you support Workers\' Compensation transportation?', answer: 'Yes. MoveraCare coordinates authorized non-emergency transportation for injured workers to approved medical, therapy, and diagnostic destinations.' },
              { question: 'Do you offer dialysis transportation?', answer: 'Yes. We provide recurring and scheduled dialysis transportation. We confirm each pickup time when dispatch accepts the booking.' },
              { question: 'Can healthcare facilities partner with MoveraCare?', answer: 'Yes. Facilities across Greater Houston — hospitals, SNFs, dialysis centers, TPAs, NEMT brokers, and Workers\' Compensation programs — may contact us about scheduled non-emergency transportation. Visit our Healthcare Partners page or Procurement Portal to learn more.' },
              { question: 'Are you licensed and insured?', answer: 'Yes. MoveraCare maintains required licensing and commercial insurance coverage for safe operations. Full documentation is available in our Procurement Packet.' },
              { question: 'How do I pay for a ride?', answer: 'Payment arrangements and any required authorization are confirmed before the trip. Private-pay and authorized partner trips may be available; payment options may include Zelle and check, and ACH bank transfer will be available after setup. Contact dispatch to confirm the payment method for your ride.' },
            ]}
            defaultOpen={0}
          />

          <div className="text-center mt-10">
            <Link to="/faq">
              <Button variant="outline" size="lg" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold" data-testid="cta-view-all-faqs">
                View All Questions
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
